import { useEffect, useState } from 'react';
import { useNavigate, useParams, useLocation, Link } from 'react-router-dom';
import {
  ArrowRightCircle,
  CheckCircle,
  ExternalLink,
  FileText,
  Loader2,
  Pencil,
  Receipt,
  Send,
  Share2,
  Trash2,
  XCircle,
} from 'lucide-react';
import { format } from 'date-fns';

import PageHeader from '../components/PageHeader.jsx';
import { useConfirm } from '../components/ConfirmDialog.jsx';
import { listBatches } from '../api/batches';
import {
  acceptQuote,
  convertQuoteToSale,
  deleteQuote,
  getQuote,
  getQuotePdfBlob,
  rejectQuote,
  sendQuote,
} from '../api/quotes';
import { canShareFiles, sharePdfBlob } from '../utils/share';

/**
 * Quote detail — mirrors SaleDetail. Shows status + customer, line items,
 * pricing summary, and a "View proforma" button that fetches the PDF via
 * authenticated apiClient and opens in a new tab.
 *
 * NewQuote redirects here after a successful create (with a flash message
 * in router state); same page also serves browse-from-list traffic.
 */

// Must match Quotes.jsx (and the backend enum: draft | submitted | accepted
// | rejected | converted) so the same row reads identically across views.
const STATUS_PILL = {
  draft:      'bg-gray-100 text-gray-700',
  submitted:  'bg-blue-100 text-blue-700',
  accepted:   'bg-green-100 text-green-700',
  rejected:   'bg-red-100 text-red-700',
  converted:  'bg-purple-100 text-purple-700',
};

function StatusPill({ status }) {
  const cls = STATUS_PILL[status] ?? 'bg-gray-100 text-gray-700';
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-pill text-caption font-medium capitalize ${cls}`}>
      {status?.replace(/_/g, ' ') ?? 'unknown'}
    </span>
  );
}

function fmt(n) {
  if (n == null || Number.isNaN(Number(n))) return '—';
  return Number(n).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function fmtDate(iso) {
  if (!iso) return '—';
  try {
    return format(new Date(iso), 'd MMM yyyy');
  } catch {
    return '—';
  }
}

export default function QuoteDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const confirm = useConfirm();
  const flash = location.state?.flash;

  const [quote, setQuote] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [pdfLoading, setPdfLoading] = useState(false);
  const [pdfError, setPdfError] = useState(null);

  // Web Share API — independent state from view-pdf so a share-in-flight
  // doesn't block the rep from also tapping View, and vice versa.
  const [pdfSharing, setPdfSharing] = useState(false);
  const shareSupported = canShareFiles();

  const [converting, setConverting] = useState(false);
  const [convertError, setConvertError] = useState(null);

  // Single in-flight transition at a time — disables ALL transition
  // buttons while one is pending so the user can't double-click between
  // them. `transition` holds the action label so the right button shows
  // a spinner. `delete` is gated by sale.status === 'draft' (matches
  // backend); on success we navigate away so no re-render is needed.
  const [transition, setTransition] = useState(null); // 'send' | 'accept' | 'reject' | 'delete' | null
  const [transitionError, setTransitionError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    getQuote(id)
      .then((data) => {
        if (!cancelled) setQuote(data);
      })
      .catch((err) => {
        if (!cancelled) {
          setError(err.response?.data?.detail || err.message || 'Failed to load quote');
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [id]);

  if (loading) {
    return (
      <main className="min-h-full px-4 pb-6">
        <PageHeader title="Quote" icon={FileText} />
        <p className="text-small text-gray-500">Loading…</p>
      </main>
    );
  }

  if (error || !quote) {
    return (
      <main className="min-h-full px-4 pb-6">
        <PageHeader title="Quote" icon={FileText} />
        <p role="alert" className="text-small text-danger">
          {error || 'Quote not found.'}
        </p>
      </main>
    );
  }

  async function openPdf() {
    setPdfLoading(true);
    setPdfError(null);
    try {
      const blob = await getQuotePdfBlob(quote.id);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.target = '_blank';
      a.rel = 'noopener noreferrer';
      document.body.appendChild(a);
      a.click();
      a.remove();
      // 5s is enough for any browser (incl. iOS Safari) to have
      // fetched the blob bytes into the new tab — see the matching
      // comment on SaleDetail.openPdf.
      setTimeout(() => URL.revokeObjectURL(url), 5_000);
    } catch (err) {
      setPdfError(
        err.response?.data?.detail || err.message || 'Failed to load proforma PDF',
      );
    } finally {
      setPdfLoading(false);
    }
  }

  /**
   * Share the quote PDF via the native OS share sheet (WhatsApp /
   * email / AirDrop). Same flow as SaleDetail's onSharePdf — fetches
   * the bytes via the authenticated apiClient, hands the blob to the
   * Web Share API helper. The share button is only rendered on
   * platforms that support file sharing.
   */
  async function onSharePdf() {
    setPdfSharing(true);
    setPdfError(null);
    try {
      const blob = await getQuotePdfBlob(quote.id);
      const filename = `quote-${quote.quote_number ?? quote.id}.pdf`;
      const result = await sharePdfBlob(blob, filename, {
        title: `Quote ${quote.quote_number ?? `#${quote.id}`}`,
        text:  quote.customer_name
          ? `Quote for ${quote.customer_name}`
          : undefined,
      });
      if (result.outcome === 'error') {
        setPdfError(result.error?.message || 'Failed to share PDF');
      } else if (result.outcome === 'unsupported') {
        setPdfError('Sharing isn’t supported on this device.');
      }
    } catch (err) {
      setPdfError(
        err.response?.data?.detail || err.message || 'Failed to share quote',
      );
    } finally {
      setPdfSharing(false);
    }
  }

  /**
   * Hard-delete this draft quote.
   *
   * Backend `DELETE /quotes/{id}` (require_sales_ops): cascade-deletes
   * line items, returns 204. Allows draft OR rejected; the mobile UX
   * gates to draft-only for consistency with the sales delete (use
   * desktop for rejected-quote cleanup).
   *
   * After success there's nothing left here, so navigate to /quotes
   * with a flash.
   */
  async function onDelete() {
    const ok = await confirm({
      title:        'Delete quote',
      message:      `Delete ${quote.quote_number ?? `Quote #${quote.id}`}?\n\nThis cannot be undone.`,
      confirmLabel: 'Delete',
      confirmVariant: 'danger',
    });
    if (!ok) return;

    setTransition('delete');
    setTransitionError(null);
    try {
      await deleteQuote(quote.id);
      navigate('/quotes', {
        replace: true,
        state: {
          flash: `${quote.quote_number ?? `Quote #${quote.id}`} deleted`,
        },
      });
    } catch (err) {
      setTransitionError(
        err.response?.data?.detail || err.message || 'Failed to delete quote',
      );
      setTransition(null);
    }
    // Note: no setTransition(null) on success — the page is unmounting.
  }

  /**
   * Convert this quote into a draft Sale.
   *
   * For each line item, pull batches with available stock (sorted FIFO —
   * oldest id first) and greedily fill the line's quantity from oldest
   * batches first. If a line's total stock < requested quantity, abort with
   * a clear error pointing to desktop for manual batch picking.
   *
   * Backend takes ConvertToSaleRequest = { batch_lines: [{product_id,
   * batch_id, quantity}] } where the same product can appear multiple times
   * (once per batch the quantity is drawn from). On success we navigate to
   * the new sale's detail page so the rep can immediately see + send the
   * proforma in one motion.
   */
  async function onConvertToSale() {
    const ok = await confirm({
      title:          'Convert to Sale',
      message:        'Convert this quote into a draft sale?\n\nStock will be reserved automatically from the oldest batches first.',
      confirmLabel:   'Convert',
      cancelLabel:    'Cancel',
      confirmVariant: 'primary',
    });
    if (!ok) return;

    setConverting(true);
    setConvertError(null);

    try {
      // Aggregate any duplicate product_ids into a single per-product
      // requirement (callers shouldn't send duplicates but be defensive).
      const productNeed = new Map(); // product_id -> { needed, name }
      for (const li of quote.line_items || []) {
        const prev = productNeed.get(li.product_id) || { needed: 0, name: li.product_name };
        productNeed.set(li.product_id, {
          needed: prev.needed + (li.quantity || 0),
          name:   prev.name,
        });
      }

      // Fetch in-stock batches for every product in parallel; map them.
      const productIds = Array.from(productNeed.keys());
      const batchLists = await Promise.all(
        productIds.map((pid) => listBatches({ productId: pid, hasStock: true })),
      );

      const batchLines = [];
      const shortages = [];

      productIds.forEach((pid, idx) => {
        const { needed, name } = productNeed.get(pid);
        // FIFO: oldest batch (lowest id) first.
        const batches = [...(batchLists[idx] || [])].sort((a, b) => a.id - b.id);

        let remaining = needed;
        for (const batch of batches) {
          if (remaining <= 0) break;
          const take = Math.min(remaining, batch.available_qty);
          if (take <= 0) continue;
          batchLines.push({
            product_id: pid,
            batch_id:   batch.id,
            quantity:   take,
          });
          remaining -= take;
        }

        if (remaining > 0) {
          shortages.push(
            `${name ?? `Product #${pid}`}: short by ${remaining} unit${remaining === 1 ? '' : 's'}`,
          );
        }
      });

      if (shortages.length > 0) {
        throw new Error(
          `Insufficient stock to convert this quote:\n\n${shortages.join('\n')}\n\nManually pick batches on the desktop ERP, or restock and try again.`,
        );
      }

      const result = await convertQuoteToSale(quote.id, batchLines);
      // Replace history so back from the new sale lands on the quote, not
      // back to the convert action which would be a no-op now.
      navigate(`/sales/${result.sale_id}`, {
        replace: true,
        state: {
          flash: `Converted to sale ${result.invoice_number ?? '#' + result.sale_id}`,
        },
      });
    } catch (err) {
      setConvertError(
        err.response?.data?.detail || err.message || 'Failed to convert quote',
      );
      setConverting(false);
    }
  }

  /**
   * Apply a quote lifecycle transition (send / accept / reject).
   *
   * Backend returns the updated QuoteOut on success — we drop it straight
   * into local state so the page reflects the new status and the
   * conditional UI re-renders to expose the next valid action.
   *
   * `confirmConfig` is forwarded to the in-app confirm modal (title,
   * message, button labels, variant). Reject uses the danger variant; the
   * others stay primary amber.
   */
  async function applyTransition(action, apiFn, confirmConfig) {
    const ok = await confirm(confirmConfig);
    if (!ok) return;
    setTransition(action);
    setTransitionError(null);
    try {
      const updated = await apiFn(quote.id);
      setQuote(updated);
    } catch (err) {
      setTransitionError(
        err.response?.data?.detail || err.message || `Failed to ${action} quote`,
      );
    } finally {
      setTransition(null);
    }
  }

  // Buttons shown in the actions section depend on the current status.
  // Backend rules (backend/app/api/routers/quotes.py):
  //   - send:    only from draft
  //   - accept:  from submitted OR draft (instant on-site agreement)
  //   - reject:  anything except converted
  //   - convert: only from accepted (and not already converted)
  const canSend       = quote.status === 'draft';
  const canAccept     = ['draft', 'submitted'].includes(quote.status);
  const canReject     = !['converted', 'rejected'].includes(quote.status);
  const canConvert    = quote.status === 'accepted' && !quote.converted_to_sale_id;
  const alreadyConverted =
    quote.status === 'converted' && quote.converted_to_sale_id;
  // Edit + delete are both draft-only (backend gate). Mirrors SaleDetail.
  const canEdit       = quote.status === 'draft';
  const canDelete     = quote.status === 'draft';
  const transitionInFlight = transition !== null;

  return (
    <main className="min-h-full px-4 pb-6">
      <PageHeader
        title={quote.quote_number ?? `Quote #${quote.id}`}
        subtitle={quote.customer_name ?? '—'}
        icon={FileText}
        right={canDelete ? (
          // Trash2 in the header right slot — only when draft (backend
          // gates DELETE to draft/rejected; mobile gates more
          // conservatively to draft only). Same pattern as SaleDetail.
          <button
            type="button"
            onClick={onDelete}
            disabled={transitionInFlight}
            aria-label="Delete quote"
            className="-mr-2 min-h-touch min-w-touch flex items-center justify-center text-white/90 active:text-white disabled:opacity-60"
          >
            {transition === 'delete' ? (
              <Loader2 className="h-5 w-5 animate-spin" />
            ) : (
              <Trash2 className="h-5 w-5" />
            )}
          </button>
        ) : null}
      />

      {flash && (
        <div role="status" className="mb-3 mb-card bg-green-50 border-green-200 text-small text-green-900">
          {flash}
        </div>
      )}

      {/* Status + date */}
      <section className="mb-card mb-3 flex items-center justify-between">
        <div>
          <p className="text-caption text-gray-500">Status</p>
          <div className="mt-1"><StatusPill status={quote.status} /></div>
        </div>
        <div className="text-right">
          <p className="text-caption text-gray-500">Date</p>
          <p className="mt-1 text-body font-medium text-gray-900">{fmtDate(quote.date)}</p>
        </div>
      </section>

      {/* Line items */}
      <section className="mb-card mb-3">
        <div className="flex items-center justify-between mb-2">
          <p className="text-small font-medium text-gray-700">Line items</p>
          {/* Inline edit affordance — only meaningful while draft
              (backend gates PATCH to draft). Same icon-only pattern as
              SaleDetail. */}
          {canEdit && (
            <Link
              to={`/quotes/${quote.id}/edit`}
              aria-label="Edit line items"
              className="min-h-touch min-w-touch -mr-2 flex items-center justify-center text-amber-700 active:text-amber-800"
            >
              <Pencil className="h-4 w-4" />
            </Link>
          )}
        </div>
        <ul className="divide-y divide-gray-200">
          {(quote.line_items || []).map((li) => (
            <li key={li.id} className="py-2 first:pt-0 last:pb-0">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <p className="text-body text-gray-900 truncate">
                    {li.product_name ?? `Product #${li.product_id}`}
                  </p>
                  <p className="text-caption text-gray-500">
                    {li.quantity} × {fmt(li.unit_price_usd)}
                  </p>
                </div>
                <p className="text-body font-medium text-gray-900 whitespace-nowrap">
                  {fmt((li.quantity || 0) * (li.unit_price_usd || 0))}
                </p>
              </div>
            </li>
          ))}
        </ul>
      </section>

      {/* Totals */}
      <section className="mb-card mb-3 space-y-1">
        {quote.subtotal_usd != null && (
          <div className="flex justify-between text-small text-gray-600">
            <span>Subtotal (ex VAT)</span>
            <span>{fmt(quote.subtotal_usd)}</span>
          </div>
        )}
        {quote.vat_amount != null && (
          <div className="flex justify-between text-small text-gray-600">
            <span>VAT{quote.vat_rate ? ` (${(quote.vat_rate * 100).toFixed(1)}%)` : ''}</span>
            <span>{fmt(quote.vat_amount)}</span>
          </div>
        )}
        <div className="pt-2 mt-2 border-t border-gray-200 flex justify-between text-title font-semibold text-gray-900">
          <span>Total</span>
          <span>{fmt(quote.final_quote_price)}</span>
        </div>
      </section>

      {/* Notes */}
      {quote.notes && (
        <section className="mb-card mb-3">
          <p className="text-small font-medium text-gray-700 mb-1">Notes</p>
          <p className="text-small text-gray-700 whitespace-pre-wrap">{quote.notes}</p>
        </section>
      )}

      {/* Actions — proforma always (with native-share button on
          supported devices); lifecycle transitions + convert/view-sale
          conditional on status. Edit + delete live on the PageHeader
          right slot / Line items header now (icon-only). */}
      <section className="space-y-2">
        <div className="flex gap-2">
          <button
            type="button"
            onClick={openPdf}
            disabled={pdfLoading}
            className="mb-card-tap flex items-center gap-3 flex-1 text-left disabled:opacity-60"
          >
            <span className="w-10 h-10 rounded-card bg-indigo-50 flex items-center justify-center shrink-0">
              {pdfLoading ? (
                <Loader2 className="h-5 w-5 text-indigo-700 animate-spin" />
              ) : (
                <FileText className="h-5 w-5 text-indigo-700" />
              )}
            </span>
            <div className="min-w-0 flex-1">
              <p className="text-body font-medium text-gray-900">View quote</p>
              <p className="text-caption text-gray-500">
                {pdfLoading ? 'Generating PDF…' : 'PDF · opens in browser'}
              </p>
            </div>
            {!shareSupported && (
              <ExternalLink className="h-4 w-4 text-gray-400 shrink-0" />
            )}
          </button>
          {shareSupported && (
            <button
              type="button"
              onClick={onSharePdf}
              disabled={pdfSharing}
              aria-label="Share quote"
              className="mb-card-tap min-w-touch flex items-center justify-center disabled:opacity-60"
            >
              {pdfSharing ? (
                <Loader2 className="h-5 w-5 text-indigo-700 animate-spin" />
              ) : (
                <Share2 className="h-5 w-5 text-indigo-700" />
              )}
            </button>
          )}
        </div>

        {pdfError && (
          <p role="alert" className="text-small text-danger px-1">
            {pdfError}
          </p>
        )}

        {/* Lifecycle transitions — Send / Accept / Reject. Each is disabled
            while ANY transition is in flight (transitionInFlight) to prevent
            double-clicks racing across buttons. The icon swaps to a spinner
            for the specific action that's pending. */}
        {canSend && (
          <button
            type="button"
            onClick={() => applyTransition('send', sendQuote, {
              title:        'Send to customer',
              message:      'Mark this quote as sent to the customer?',
              confirmLabel: 'Send',
            })}
            disabled={transitionInFlight}
            className="mb-card-tap flex items-center gap-3 w-full text-left disabled:opacity-60"
          >
            <span className="w-10 h-10 rounded-card bg-blue-50 flex items-center justify-center shrink-0">
              {transition === 'send' ? (
                <Loader2 className="h-5 w-5 text-blue-700 animate-spin" />
              ) : (
                <Send className="h-5 w-5 text-blue-700" />
              )}
            </span>
            <div className="min-w-0 flex-1">
              <p className="text-body font-medium text-gray-900">Send to customer</p>
              <p className="text-caption text-gray-500">Marks the quote as submitted</p>
            </div>
          </button>
        )}

        {canAccept && (
          <button
            type="button"
            onClick={() => applyTransition('accept', acceptQuote, {
              title:        'Mark as accepted',
              message:      'Mark this quote as accepted by the customer?\n\nYou’ll then be able to convert it to a draft sale.',
              confirmLabel: 'Mark accepted',
              confirmVariant: 'success',
            })}
            disabled={transitionInFlight}
            className="mb-card-tap flex items-center gap-3 w-full text-left disabled:opacity-60"
          >
            <span className="w-10 h-10 rounded-card bg-green-50 flex items-center justify-center shrink-0">
              {transition === 'accept' ? (
                <Loader2 className="h-5 w-5 text-green-700 animate-spin" />
              ) : (
                <CheckCircle className="h-5 w-5 text-green-700" />
              )}
            </span>
            <div className="min-w-0 flex-1">
              <p className="text-body font-medium text-gray-900">Mark accepted</p>
              <p className="text-caption text-gray-500">
                Customer agreed · ready to convert to a sale
              </p>
            </div>
          </button>
        )}

        {canReject && (
          <button
            type="button"
            onClick={() => applyTransition('reject', rejectQuote, {
              title:          'Mark as rejected',
              message:        'Mark this quote as rejected?\n\nIt will not be convertible to a sale after this.',
              confirmLabel:   'Reject',
              confirmVariant: 'danger',
            })}
            disabled={transitionInFlight}
            className="mb-card-tap flex items-center gap-3 w-full text-left disabled:opacity-60"
          >
            <span className="w-10 h-10 rounded-card bg-red-50 flex items-center justify-center shrink-0">
              {transition === 'reject' ? (
                <Loader2 className="h-5 w-5 text-red-700 animate-spin" />
              ) : (
                <XCircle className="h-5 w-5 text-red-700" />
              )}
            </span>
            <div className="min-w-0 flex-1">
              <p className="text-body font-medium text-gray-900">Mark rejected</p>
              <p className="text-caption text-gray-500">Customer declined</p>
            </div>
          </button>
        )}

        {transitionError && (
          <p role="alert" className="text-small text-danger px-1">
            {transitionError}
          </p>
        )}

        {/* Convert-to-sale: filled amber to signal it's the primary next
            action when the quote is accepted. Auto-allocates batches FIFO
            from oldest available stock and lands the user on the new sale's
            detail page. */}
        {canConvert && (
          <button
            type="button"
            onClick={onConvertToSale}
            disabled={converting}
            className="w-full flex items-center gap-3 rounded-card bg-amber-500 hover:bg-amber-600 active:bg-amber-600 text-white shadow-lg p-4 disabled:opacity-60 active:scale-[0.99] transition-transform"
          >
            <span className="w-10 h-10 rounded-card bg-white/15 flex items-center justify-center shrink-0">
              {converting ? (
                <Loader2 className="h-5 w-5 animate-spin" />
              ) : (
                <ArrowRightCircle className="h-5 w-5" />
              )}
            </span>
            <div className="min-w-0 flex-1 text-left">
              <p className="text-body font-semibold">
                {converting ? 'Converting…' : 'Convert to Sale'}
              </p>
              <p className="text-caption text-white/80">
                {converting
                  ? 'Reserving stock from oldest batches'
                  : 'Creates a draft sale · stock reserved FIFO'}
              </p>
            </div>
          </button>
        )}

        {convertError && (
          <p
            role="alert"
            className="text-small text-danger px-1 whitespace-pre-line"
          >
            {convertError}
          </p>
        )}

        {/* When a quote has already been converted, link straight to the
            resulting sale instead of offering a duplicate convert action. */}
        {alreadyConverted && (
          <Link
            to={`/sales/${quote.converted_to_sale_id}`}
            className="mb-card-tap flex items-center gap-3 no-underline"
          >
            <span className="w-10 h-10 rounded-card bg-purple-50 flex items-center justify-center shrink-0">
              <Receipt className="h-5 w-5 text-purple-700" />
            </span>
            <div className="min-w-0 flex-1">
              <p className="text-body font-medium text-gray-900">View linked sale</p>
              <p className="text-caption text-gray-500">
                This quote was converted to sale #{quote.converted_to_sale_id}
              </p>
            </div>
            <ExternalLink className="h-4 w-4 text-gray-400 shrink-0" />
          </Link>
        )}

        {/* Status hint — only for terminal rejected state since the button
            row already makes the available actions explicit for everything
            else. */}
        {quote.status === 'rejected' && (
          <p className="text-caption text-gray-500 px-1">
            This quote was rejected and cannot be converted.
          </p>
        )}
      </section>
    </main>
  );
}
